/**
 * Wipes browser-side customer data when the dev area takes over a tab.
 *
 * The dev area never reads these keys, but leaving them resident would:
 *   1. Keep a prior customer's filing state / pending-user-update list
 *      around across an explicit session boundary.
 *   2. Mislead any future "customer" login in the same tab with stale
 *      partially-completed forms.
 *   3. Grow storage unboundedly across many dev logins.
 *
 * Centralising the key inventory here keeps the "what counts as customer
 * data" knowledge in one place. Add new prefixes here the moment a new
 * customer-scoped storage key is introduced anywhere in the app — the
 * dev-login code path does not need to change.
 *
 * Call site today: `AuthService.devLogin()` after a successful
 * `DevSessionService.setSession(...)`.
 *
 * NOT cleared by this helper:
 *   - The HttpOnly identity cookie (server-owned, cannot be touched
 *     from JS anyway; a dev login is orthogonal to the customer auth).
 *   - `yyc_dev_session` — that is the dev-area cookie we are in the
 *     middle of establishing.
 *   - In-memory signals on `providedIn: 'root'` services — acceptable
 *     because dev routes do not render customer components, and the
 *     services rehydrate from localStorage on the next customer visit,
 *     which will now find the storage empty.
 */

/**
 * localStorage + sessionStorage key prefixes owned by the customer flows.
 * Any key whose name starts with one of these is eligible for removal.
 */
const CUSTOMER_KEY_PREFIXES: readonly string[] = [
  'mbrs-',
  '__mbrs-',
];

/**
 * Exact-match customer storage keys that do NOT carry one of the prefixes
 * above. Keep this list small — prefer prefixing any future keys.
 */
const CUSTOMER_EXACT_KEYS: readonly string[] = [
  'filingSessionId',              // FilingSessionStore (sessionStorage)
  'user-management-pending-updates', // user-management component (localStorage)
];

function collectMatchingKeys(storage: Storage): string[] {
  const out: string[] = [];
  for (let storageIndex = 0; storageIndex < storage.length; storageIndex++) {
    const key = storage.key(storageIndex);
    if (!key) continue;
    if (
      CUSTOMER_EXACT_KEYS.includes(key) ||
      CUSTOMER_KEY_PREFIXES.some((prefix) => key.startsWith(prefix))
    ) {
      out.push(key);
    }
  }
  return out;
}

/**
 * Removes every customer-owned entry from both `localStorage` and
 * `sessionStorage`. Safe to call multiple times; no-op in SSR / non-
 * browser contexts.
 *
 * NOTE: this util is DI-free and does NOT touch IndexedDB. The filing-form
 * per-conversion state now lives in IndexedDB; it is wiped separately by the
 * DI-capable teardown callers via `FilingFormPersistenceService.clearAll()`
 * (using the service's already-open connection — never `deleteDB`, which would
 * block on that connection). See `AuthService.clearAuthState()` and
 * `DevSessionService.setSession()`.
 */
export function clearCustomerDataCache(): void {
  if (typeof window === 'undefined') {
    return;
  }
  for (const storage of [window.localStorage, window.sessionStorage]) {
    try {
      const keys = collectMatchingKeys(storage);
      for (const key of keys) {
        storage.removeItem(key);
      }
    } catch {
      // Private-mode browsers may refuse storage access; ignore.
    }
  }
}
